<template>
	<div class="h-100">
		<default-layout :is-aligned="false">
			<template #default>
				<div class="header">
					<slot name="logo">
						<img src="/images/hp-logo.svg" alt="HP" />
					</slot>
				</div>
				<b-card
					class="mx-auto"
					header-tag="header"
					align="center"
					style="max-width: 55rem; display: inline-block;"
					body-bg-variant="primary"
					body-class="body"
					footer-class="footer"
				>
					<b-card-text class="text-left">
						<h6 v-if="title" class="title text-center">{{ $t(title) }}</h6>
						<template v-if="code === 1001">
							<p v-t="'The page you requested could not be found.'" />
							<ul>
								<li>
									{{ $t('The page address may be incorrect.') }}
									<em v-if="!isInvitationRoute"> {{ $t(`Go back to ${appName} and request the page again.`) }}</em>
								</li>
								<li
									v-if="isInvitationRoute"
									v-t="'The invitation link might be missing information. Retry the invitation link.'"
								/>
								<li>
									{{ $t(`You may be able to sign in to ${appName} directly.`) }}
									<em v-if="!isInvitationRoute"> {{ $t(`Go to ${appName} and sign in.`) }}</em>
								</li>
							</ul>
							<p>
								{{ $t('Still having issues?') }} <a :href="mailto">{{ $t('Contact HP Support') }}</a>
							</p>
						</template>
						<template v-else-if="code === 1002 || code === 1003">
							<p v-t="`An unexpected error happened and it is not possible to return to ${appName}.`" />
							<ul v-if="!isInvitationRoute">
								<li>
									{{ $t(`Go to ${appName} and repeat the process.`) }}
								</li>
							</ul>
							<ul>
								<li>{{ $t('If the issue persist,') }} <a :href="mailto">contact HP Support</a></li>
							</ul>
						</template>
						<template v-else-if="code === 1004">
							<p v-t="'The requested view was denied because your user account lacks authorization.'" />
							<ul>
								<li>
									{{ $t('Your user role might not support the requested view.') }}
									<em>{{ $t('Ask the administrator to review your account.') }}</em>
								</li>
								<li>
									{{ $t('Still having issues?') }} <a :href="mailto">{{ $t('Contact HP Support') }}</a>
								</li>
							</ul>
						</template>
						<template
							v-else-if="
								code === 1005 &&
									(message === 'No matching primary email found' ||
										message === 'Primary email linked to another HP account')
							"
						>
							<p v-t="`Your HP account${email ? ` <${email}>` : ''} is not registered with ${appName}.`" />
							<ul>
								<li>
									{{ $t(`You might have an HP account with a different email that is registered with ${appName}.`) }}
									<em>{{ $t('Sign in with that account.') }}</em>
								</li>
								<li>
									{{ $t(`You might not have received or accepted an invitation from ${appName}.`) }}
									<em>{{ $t('Ask the administrator to resend the invitation.') }}</em>
								</li>
								<li>
									{{ $t('Still having issues?') }} <a :href="mailto">{{ $t('Contact HP Support') }}</a>
								</li>
							</ul>
						</template>
						<template v-else-if="code === 1005 || code === 1007">
							<p>{{ $t(message) }}</p>
							<p>
								<a :href="mailto">{{ $t('Contact HP Support') }}</a>
							</p>
						</template>
						<template
							v-else-if="
								code === 1006 && (message === 'Invitation already used or expired' || message === 'Invitation Invalid')
							"
						>
							<p v-t="'The invitation has already been accepted or has expired.'" />
							<ul>
								<li>
									{{ $t(`You may have already accepted the invitation.`) }}
									<em>{{ $t(`Sign in into ${appName}.`) }}</em>
								</li>
								<li>
									{{ $t('The invitation may have expired.') }}
									<em>{{ $t('Ask the administrator to resend the invitation.') }}</em>
								</li>
								<li>
									{{ $t('Still having issues?') }} <a :href="mailto">{{ $t('Contact HP Support') }}</a>
								</li>
							</ul>
						</template>
						<template v-else>
							<p>
								<a :href="mailto">{{ $t('Contact HP Support') }}</a>
							</p>
						</template>
					</b-card-text>
					<template v-if="redirectUrl && (!isInvitationRoute || code === 1006)" #footer>
						<div class="text-left">
							<b-button block variant="outline-primary" @click="logoutAndRedirect">
								{{ !isInvitationRoute ? $t(`Back to ${appName}`) : $t(`Go to ${appName}`) }}
							</b-button>
						</div>
					</template>
				</b-card>
			</template>
		</default-layout>
		<Footer />
	</div>
</template>

<script type="text/javascript">
import DefaultLayout from './DefaultLayout';
import Footer from './Footer';

export default {
	name: 'ErrorMessage',
	components: {
		DefaultLayout,
		Footer
	},
	props: {
		code: {
			type: Number,
			default: 1000
		},
		message: {
			type: String,
			default: ''
		},
		appInfo: {
			type: Object,
			default: () => ({})
		},
		title: {
			type: String,
			default: ''
		},
		email: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			appName: 'the application',
			redirectUrl: ''
		};
	},
	computed: {
		isInvitationRoute() {
			return ['invitation', 'invitationComplete'].includes(this.$route.name);
		},
		mailto() {
			let mailto = 'mailto:siteflowsupport@hp.com?subject=' + encodeURIComponent(this.$t(`Error code: ${this.code}`));
			if (this.message) mailto += `&body=${encodeURIComponent(this.$t(this.message))}`;
			return mailto;
		}
	},
	watch: {
		appInfo: {
			immediate: true,
			handler({ redirectUrl, appName }) {
				if (redirectUrl) this.redirectUrl = redirectUrl;
				if (appName) this.appName = appName;
			}
		}
	},
	methods: {
		logoutAndRedirect() {
			const { app_name } = this.appInfo;
			if (this.$route.name !== 'logout') this.$router.push({ name: 'logout', query: { app_name } });
			if (this.redirectUrl) window.location.href = this.redirectUrl;
		}
	}
};
</script>
<style lang="scss" scoped>
$of-color-white: #ffffff !default;
$of-color-hp-brand: #0088d3 !default;
$of-color-aqua: #23b7e5 !default;
.header {
	margin: 40px;
}
.card {
	background-color: $of-color-white !important;
	border-radius: 3px !important;
	border: none !important;
}
.body {
	margin: 40px;
}
.title {
	color: #c81818;
	margin-bottom: 20px;
}
img {
	height: 40px !important;
}
a {
	color: $of-color-aqua;
}
.bg-primary {
	background-color: #f9e5e5 !important;
	border-radius: 3px;
}
li {
	margin-top: 10px;
	margin-bottom: 10px;
}
.btn-outline-primary {
	background: $of-color-hp-brand;
	color: $of-color-white;
	border-color: $of-color-hp-brand;

	&:hover:not(:disabled):not(.disabled) {
		background: lighten($of-color-hp-brand, 5%);
		border-color: lighten($of-color-hp-brand, 5%);
	}

	&:disabled {
		background: lighten($of-color-hp-brand, 15%);
		border-color: lighten($of-color-hp-brand, 15%);
	}
}
.footer {
	margin: -20px 20px 20px 20px;
	border-top: none;
	background: $of-color-white;
}
</style>
